<template>
  <b-card>
    <b-button variant="link" class="p-0 mb-1" @click="goBack">
      <feather-icon icon="ArrowLeftIcon" size="16" /> Volver a Reservas Xcalacoco
    </b-button>

    <div v-if="isLoading && !booking" class="text-center my-3">
      <b-spinner class="align-middle" /> <strong>Cargando reserva</strong>
    </div>

    <b-alert v-else-if="!booking" show variant="warning" class="p-1">No se encontró la reserva.</b-alert>

    <template v-else>
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
        <h3 class="mb-0">
          Reserva {{ booking.folio }}
          <b-badge pill :class="'bg-' + statusVariant(booking.status)" class="ml-50 align-middle">{{ booking.statusname }}</b-badge>
        </h3>
        <div class="mt-1 mt-md-0">
          <b-button v-if="canEdit" variant="outline-primary" size="sm" class="mr-50" :disabled="isSaving" @click="editModal = { visible: true }">
            <feather-icon icon="EditIcon" size="14" /> Editar
          </b-button>
          <b-button v-if="canConfirm" variant="success" size="sm" class="mr-50" :disabled="isSaving" @click="openStatusModal('confirm')">
            <feather-icon icon="CheckCircleIcon" size="14" /> Confirmar
          </b-button>
          <b-button v-if="canCancel" variant="outline-danger" size="sm" :disabled="isSaving" @click="openStatusModal('cancel')">
            <feather-icon icon="XCircleIcon" size="14" /> Cancelar
          </b-button>
          <b-button v-if="canReviewCancel" variant="danger" size="sm" class="mr-50" :disabled="isSaving" @click="openStatusModal('approveCancel')">
            <feather-icon icon="CheckCircleIcon" size="14" /> Aprobar cancelación
          </b-button>
          <b-button v-if="canReviewCancel" variant="outline-primary" size="sm" :disabled="isSaving" @click="openStatusModal('rejectCancel')">
            <feather-icon icon="RotateCcwIcon" size="14" /> Rechazar cancelación
          </b-button>
        </div>
      </div>

      <section v-for="section in sections" :key="section.title" class="mb-2">
        <h5 class="border-bottom pb-50 mb-1">{{ section.title }}</h5>
        <b-row>
          <div v-for="(field, index) in section.fields" :key="index" :class="field.wide ? 'col-12' : 'col-6 col-md-4 col-lg-2'" class="mb-1">
            <span v-if="field.label" class="d-block font-weight-bolder">{{ field.label }}</span>
            <span :class="{ notes: field.wide }">{{ displayValue(field.value) }}</span>
          </div>
        </b-row>
      </section>
    </template>

    <ModalOwnerBookingStatus
      :visible="statusModal.visible"
      :mode="statusModal.mode"
      :modes="statusModes"
      :booking="booking"
      :isSaving="isSaving"
      @submit="submitStatus"
      @close="closeStatusModal"
    >
      <template #summary>
        <p class="mb-1"><b>Llegada / Salida:</b> {{ booking.dateIn }} / {{ booking.dateOut }}</p>
        <p class="mb-2"><b>Adultos / Menores:</b> {{ booking.adults }} / {{ booking.children }}</p>
      </template>
    </ModalOwnerBookingStatus>

    <ModalEditXcalacocoBooking
      :visible="editModal.visible"
      :booking="booking"
      :isSaving="isSaving"
      :maxAdults="guestLimits.adults"
      :maxChildren="guestLimits.children"
      @save="saveBooking"
      @close="closeEditModal"
    />
  </b-card>
</template>

<script>
import { mapActions } from 'vuex'
import {
  formatXcalacocoDay, formatXcalacocoDateTime, xcalacocoNightsLabel, xcalacocoReasonLabel, xcalacocoStatusVariant, xcalacocoHolderName,
} from '@/helpers/xcalacocoBookingsHelper'
import { ownerBookingDetail } from '@/modules/fivesClub/mixins/ownerBookingDetail'
import ModalOwnerBookingStatus from '@/modules/fivesClub/components/catalogs/ownersBookings/ModalOwnerBookingStatus'
import ModalEditXcalacocoBooking from '@/modules/fivesClub/components/catalogs/ownersBookings/xcalacoco/ModalEditXcalacocoBooking'

export default {
  mixins: [ownerBookingDetail],
  components: {
    ModalOwnerBookingStatus,
    ModalEditXcalacocoBooking,
  },
  data() {
    return {
      listTab: 'xcalacoco',
      confirmMode: {
        title: 'Confirmar reserva',
        label: 'Localizador Avantio',
        button: 'Confirmar',
        variant: 'primary',
        body: (booking, text) => ({
          id: booking.id, status: '2', reservanumber: text, stamp: booking.stamp,
        }),
        success: 'Reserva confirmada',
      },
      actionPermissions: {
        edit: 'fivesclub_owner_booking_edit_xcalacoco_booking',
        confirm: 'fivesclub_owner_booking_confirm_xcalacoco_booking',
        cancel: 'fivesclub_owner_booking_cancel_xcalacoco_booking',
      },
      contractStatuses: [],
      guestLimits: { adults: null, children: null },
    }
  },
  computed: {
    sections() {
      const { booking } = this
      if (!booking) return []
      return [
        {
          title: 'Identificadores',
          fields: [
            { label: 'Confirmación', value: booking.folio },
            { label: 'Localizador', value: booking.reservanumber },
            { label: 'Creada', value: this.formatDateTime(booking.createDate) },
          ],
        },
        {
          title: 'Estancia',
          fields: [
            { label: 'Vivienda', value: booking.housingnumber },
            { label: 'Llegada', value: this.formatDay(booking.dateIn) },
            { label: 'Salida', value: this.formatDay(booking.dateOut) },
            { label: 'Noches', value: this.nightsLabel(booking) },
            { label: 'Motivo', value: this.reasonLabel(booking.reason) },
          ],
        },
        {
          title: 'Propietario',
          fields: [
            { label: 'Propietario', value: booking.ownerName },
            { label: 'Contrato', value: this.contractLabel(booking.contractStatus) },
          ],
        },
        {
          title: 'Huéspedes',
          fields: [
            { label: 'Adultos', value: booking.adults },
            { label: 'Menores', value: booking.children },
            { label: 'Titular', value: this.holderName(booking) },
          ],
        },
        { title: 'Solicitud especial', fields: [{ value: booking.notes, wide: true }] },
      ].concat(this.cancelRequestSection(booking))
    },
  },
  async created() {
    const [statuses, maxAdults, maxChildren] = await Promise.all([
      this.statusContract(),
      this.fetchGuestLimit(process.env.VUE_APP_XCALACOCO_MAX_ADULTS_PARAMETER_ID),
      this.fetchGuestLimit(process.env.VUE_APP_XCALACOCO_MAX_CHILDREN_PARAMETER_ID),
      this.loadBooking(),
    ])
    this.contractStatuses = Array.isArray(statuses) ? statuses : []
    this.guestLimits = { adults: maxAdults, children: maxChildren }
  },
  methods: {
    ...mapActions('start', ['getGenericParameter']),
    ...mapActions('fivesClubContracts', ['statusContract']),
    ...mapActions('fivesClubCatalogs', {
      fetchBooking: 'fetchXcalacocoBookingById',
      setBookingStatus: 'setXcalacocoBookingStatus',
      updateBooking: 'updateXcalacocoBooking',
    }),
    // Fail-open: an unset id or failed fetch means no cap, since the backend does not enforce one
    async fetchGuestLimit(parameterId) {
      if (!parameterId) return null
      const value = parseInt(await this.getGenericParameter(parameterId), 10)
      return Number.isInteger(value) && value >= 0 ? value : null
    },
    contractLabel(statusId) {
      if (statusId === null || statusId === undefined) return '—'
      const status = this.contractStatuses.find(item => String(item.id) === String(statusId))
      return status ? status.name : '—'
    },
    formatDay: formatXcalacocoDay,
    formatDateTime: formatXcalacocoDateTime,
    nightsLabel: xcalacocoNightsLabel,
    reasonLabel: xcalacocoReasonLabel,
    statusVariant: xcalacocoStatusVariant,
    holderName: xcalacocoHolderName,
  },
}
</script>

<style scoped>
.notes {
  white-space: pre-line;
}
</style>
